import createDependableResource, { Loaded } from 'utils/zustand/createDependableResource'
import { shallow } from 'zustand/shallow'
import { BigNumber } from 'bignumber.js'
import { multicallv2Encoded } from 'utils/multicall'
import { getVvsBoostFarmContract } from 'utils/contractHelpers'
import { getAccount, getSigner, onAccountInitOrChange } from 'state/web3/account'
import type { AwaitedReturnType } from 'utils/types'

interface UserBoostedFarmInfo {
  farmId: number
  boosted: boolean
  shares: BigNumber
  rewardDebt: BigNumber
  boostingNfts: string[]
  actualAdjustedShare: BigNumber
}

interface UserBoostedFarmsInfos {
  boostedFarmsInfos: Record<number, UserBoostedFarmInfo>
  boostedFarmsIds: number[]
  isNftStaking: boolean
}

const EMPTY_USER_BOOSTED_FARMS_INFOS: UserBoostedFarmsInfos = {
  boostedFarmsInfos: {},
  boostedFarmsIds: [],
  isNftStaking: false,
}

const fetchUserBoostedFarmsInfos = async (): Promise<UserBoostedFarmsInfos> => {
  const account = getAccount()
  if (!account) {
    return EMPTY_USER_BOOSTED_FARMS_INFOS
  }
  const signer = getSigner()

  const vvsBoostFarmContract = getVvsBoostFarmContract(signer)

  const boostedFarmIds = (await vvsBoostFarmContract.getBoostedFarms(account)).map((id) => id.toNumber())

  const [boostedFarmUserInfos, actualAdjustedShares] = await Promise.all([
    multicallv2Encoded<AwaitedReturnType<typeof vvsBoostFarmContract['getUserInfo']>[]>(
      boostedFarmIds.map((farmId) => [
        vvsBoostFarmContract,
        'getUserInfo',
        vvsBoostFarmContract.interface.encodeFunctionData('getUserInfo', [farmId, account]),
      ]),
      signer,
    ),
    multicallv2Encoded<AwaitedReturnType<typeof vvsBoostFarmContract['getActualAdjustedShare']>[]>(
      boostedFarmIds.map((farmId) => [
        vvsBoostFarmContract,
        'getActualAdjustedShare',
        vvsBoostFarmContract.interface.encodeFunctionData('getActualAdjustedShare', [account, farmId]),
      ]),
      signer,
    ),
  ])

  const boostedFarmsInfos = boostedFarmIds.map((farmId, i) => {
    const userInfo = boostedFarmUserInfos[i]
    const actualAdjustedShare = actualAdjustedShares[i]

    return {
      farmId,
      boosted: true,
      shares: new BigNumber(userInfo.shares.toString()),
      rewardDebt: new BigNumber(userInfo.rewardDebt.toString()),
      boostingNfts: userInfo.boostingNfts.map((nft) => nft.tokenId.toString()),
      actualAdjustedShare: new BigNumber(actualAdjustedShare.toString()),
    }
  })

  return {
    boostedFarmsInfos: Object.fromEntries(boostedFarmsInfos.map((info) => [info.farmId, info])),
    boostedFarmsIds: boostedFarmsInfos.map((info) => info.farmId),
    isNftStaking: boostedFarmsInfos.some((info) => info.boostingNfts.length > 0),
  }
}

export const useUserBoostedFarmsInfos = createDependableResource<UserBoostedFarmsInfos>({
  initValue: EMPTY_USER_BOOSTED_FARMS_INFOS,
  getValue: fetchUserBoostedFarmsInfos,
  onInit: ({ resetValue, refresh, getIsStarted }) => {
    onAccountInitOrChange(() => {
      if (!getIsStarted()) return
      resetValue()
      refresh()
    })
  },
  dependResources: [],
  dbgName: 'UserBoostedFarmsInfos',
})

/**
 * return UserBoostedFarmInfo if boosted, other wise undefined
 */
export const useUserBoostedFarmsInfo = (
  farmId?: number,
): [userBoostedFarmInfo: UserBoostedFarmInfo | undefined, loaded: Loaded] => {
  return useUserBoostedFarmsInfos((s) => [s.value.boostedFarmsInfos[farmId], s.loaded], shallow)
}
